import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { Loader2, Trash2, X } from "lucide-react"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import {
  LocationScene3d,
  type LocationScene3dHandle,
  type LocationScene3dMode,
  type Scene3dEditorFocus,
} from "@/components/locations/location-scene-3d"
import type { CastRow, PropRow } from "@/components/prompt-editor/mention-sources"
import { WorkspaceFullscreenPanel } from "@/components/workspace/workspace-fullscreen-panel"
import { useTranslation } from "@/hooks/use-translation"
import type { LibraryPrevizItem } from "@/lib/library-api"
import type { SceneLayoutState, SceneObjectState } from "@/lib/location-scene-3d/scene-state"
import { cn } from "@/lib/utils"

export type LocationScene3dDialogHandle = {
  requestReset: () => void
  requestClose: () => void
  /** Leave the editor. A shot asks before dropping an unsaved flythrough. Poster upload does not block the switch. */
  requestLeave: () => Promise<boolean>
  agentFocus: () => Scene3dEditorFocus | null
  applyServerLayout: (raw: unknown) => void
  focusObject: (uid: string) => void
  editObjectPath: (uid: string) => void
  showObjectPath: (uid: string) => void
}

type LocationScene3dDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  locationId: number | null
  locationName: string
  cast?: CastRow[]
  props?: PropRow[]
  canEdit?: boolean
  onLayoutChanged?: () => void
  frameFormat?: string | null
  shotId?: number | null
  presetCharacterIds?: number[]
  onPrevizSaved?: (previz?: LibraryPrevizItem) => void
  /** Сохранить принято, до записи ролика. */
  onPrevizCreateStart?: () => void
  /** Upload превиза после закрытия редактора не удался. */
  onPrevizSaveFailed?: () => void
  /** Сразу после локального webm, до upload. Панель включает плашку подготовки. */
  onPrevizCapture?: () => void
  /** Viewpoint still saved — refresh the gallery, do not close the editor. */
  onViewpointSaved?: (viewpoint?: { id: number }) => void
  /** Подсказки на кнопках съёмки. Только сразу после создания сцены. */
  captureHints?: boolean
  nested?: boolean | 2 | 3
  resetPlacement?: "shell" | "panel-header"
  onExistsChange?: (exists: boolean) => void
  autoStartDeviceCamera?: boolean
  /** QR вида из камеры: та же модалка, гироскоп крутит камеру сцены. */
  autoStartCameraView?: boolean
  onPhoneFlyEnter?: () => void
  /** User left the phone camera modal. */
  onDeviceCameraExit?: () => void
  sceneMode?: LocationScene3dMode
  scene3dId?: number | null
  previzId?: number | null
  initialSceneState?: Partial<SceneLayoutState> | null
  initialHasVideo?: boolean
  initialHasPoster?: boolean
  modelLabel?: string | null
  /** Ignore Escape and the phone close control. The page owns when the editor goes away. */
  preventDismiss?: boolean
  sceneName: string
  onSceneNameSaved?: (name: string) => void
  /** После записи превиза закрыть редактор, не возвращаясь к сцене. */
  dismissAfterPrevizSave?: boolean
  /** Спросить про несохранённый превиз перед уходом. Только редактор на шоте. */
  confirmUnsavedPreviz?: boolean
  /** Live objects and viewport selection for the scene object list. */
  onObjectCatalogChange?: (objects: SceneObjectState[], selectedUid: string | null) => void
  /** Path edit and path preview ids, only when those ids change. */
  onObjectPathModeChange?: (mode: { editUid: string | null; previewUid: string | null }) => void
}

const FLOATING_CHROME_BTN =
  "pointer-events-auto flex h-8 w-8 items-center justify-center rounded-lg border border-white/20 bg-black/45 text-white/90 backdrop-blur-sm transition-colors hover:bg-black/70 hover:text-white"

export const LocationScene3dDialog = forwardRef<LocationScene3dDialogHandle, LocationScene3dDialogProps>(
  function LocationScene3dDialog(
    {
      open,
      onOpenChange,
      projectId,
      locationId,
      locationName,
      cast = [],
      props = [],
      canEdit = true,
      onLayoutChanged,
      frameFormat,
      shotId,
      presetCharacterIds,
      onPrevizSaved,
      onPrevizCreateStart,
      onPrevizSaveFailed,
      onPrevizCapture,
      onViewpointSaved,
      captureHints = false,
      nested = false,
      resetPlacement = "panel-header",
      onExistsChange,
      autoStartDeviceCamera = false,
      autoStartCameraView = false,
      onPhoneFlyEnter,
      onDeviceCameraExit,
      sceneMode = "newPreviz",
      scene3dId = null,
      previzId = null,
      initialSceneState = null,
      initialHasVideo = false,
      initialHasPoster = false,
      modelLabel = null,
      preventDismiss = false,
      sceneName,
      onSceneNameSaved,
      dismissAfterPrevizSave = false,
      confirmUnsavedPreviz = false,
      onObjectCatalogChange,
      onObjectPathModeChange,
    },
    ref,
  ) {
    const { t } = useTranslation(["workspace"])
    const [immersive, setImmersive] = useState(false)
    const [phoneBootDismissed, setPhoneBootDismissed] = useState(false)
    const [exists, setExists] = useState(false)
    const [previzSaving, setPrevizSaving] = useState(false)
    const [pathConfirmOpen, setPathConfirmOpen] = useState(false)
    const [closeBusy, setCloseBusy] = useState(false)
    const sceneRef = useRef<LocationScene3dHandle>(null)
    const closeBusyRef = useRef(false)
    closeBusyRef.current = closeBusy
    const pathConfirmSavingRef = useRef(false)
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange
    const leaveResolveRef = useRef<((ok: boolean) => void) | null>(null)
    const leaveHandledRef = useRef(false)
    const preventDismissRef = useRef(preventDismiss)
    preventDismissRef.current = preventDismiss
    const confirmUnsavedPrevizRef = useRef(confirmUnsavedPreviz)
    confirmUnsavedPrevizRef.current = confirmUnsavedPreviz

    const settleLeave = useCallback((ok: boolean) => {
      const resolve = leaveResolveRef.current
      leaveResolveRef.current = null
      resolve?.(ok)
    }, [])

    const flushLeave = useCallback(
      (ok: boolean) => {
        if (!ok) {
          settleLeave(false)
          return
        }
        if (closeBusyRef.current) {
          settleLeave(false)
          return
        }
        try {
          if (!sceneRef.current?.isExporting()) void sceneRef.current?.schedulePosterUpload()
        } catch {
          // A failed snapshot must not trap the scene switch.
        }
        settleLeave(true)
      },
      [settleLeave],
    )

    const finishClose = useCallback(async () => {
      if (closeBusyRef.current) return
      closeBusyRef.current = true
      setCloseBusy(true)
      try {
        if (!sceneRef.current?.isExporting()) await sceneRef.current?.uploadPosterIfNeeded()
      } finally {
        setImmersive(false)
        setPathConfirmOpen(false)
        onOpenChangeRef.current(false)
        closeBusyRef.current = false
        setCloseBusy(false)
      }
    }, [])

    const beginClose = useCallback(() => {
      if (preventDismissRef.current) return
      if (closeBusyRef.current) return
      if (confirmUnsavedPrevizRef.current && sceneRef.current?.isPathExportDirty()) {
        setPathConfirmOpen(true)
        return
      }
      void finishClose()
    }, [finishClose])

    const savePathAndLeave = useCallback(() => {
      void (async () => {
        if (closeBusyRef.current) {
          settleLeave(false)
          return
        }
        if (sceneRef.current?.isExporting()) {
          settleLeave(true)
          return
        }
        closeBusyRef.current = true
        setCloseBusy(true)
        setPathConfirmOpen(false)
        try {
          const saved = (await sceneRef.current?.savePathPreviz()) ?? false
          if (!saved) {
            settleLeave(false)
            return
          }
          try {
            void sceneRef.current?.schedulePosterUpload()
          } catch {
            // A failed snapshot must not undo a saved path or trap the switch.
          }
          settleLeave(true)
        } catch {
          settleLeave(false)
        } finally {
          closeBusyRef.current = false
          setCloseBusy(false)
        }
      })()
    }, [settleLeave])

    useImperativeHandle(
      ref,
      () => ({
        requestReset: () => sceneRef.current?.requestReset(),
        requestClose: () => beginClose(),
        requestLeave: () =>
          new Promise<boolean>((resolve) => {
            if (closeBusyRef.current) {
              resolve(false)
              return
            }
            leaveResolveRef.current = resolve
            if (confirmUnsavedPrevizRef.current && sceneRef.current?.isPathExportDirty()) {
              setPathConfirmOpen(true)
              return
            }
            void flushLeave(true)
          }),
        agentFocus: () => sceneRef.current?.agentFocus() ?? null,
        applyServerLayout: (raw) => sceneRef.current?.applyServerLayout(raw),
        focusObject: (uid: string) => sceneRef.current?.focusObject(uid),
        editObjectPath: (uid: string) => sceneRef.current?.editObjectPath(uid),
        showObjectPath: (uid: string) => sceneRef.current?.showObjectPath(uid),
      }),
      [beginClose, flushLeave],
    )

    const handleExistsChange = useCallback(
      (next: boolean) => {
        setExists(next)
        onExistsChange?.(next)
      },
      [onExistsChange],
    )

    useEffect(() => {
      if (!open) {
        setExists(false)
        setImmersive(false)
        setPhoneBootDismissed(false)
        setPrevizSaving(false)
        setPathConfirmOpen(false)
        setCloseBusy(false)
        onExistsChange?.(false)
      }
    }, [open, onExistsChange])

    const phoneAutoStart = canEdit && (autoStartDeviceCamera || autoStartCameraView) && !phoneBootDismissed
    const waitingForScene = phoneAutoStart && scene3dId == null
    const mountScene = scene3dId != null || (!phoneBootDismissed && !autoStartDeviceCamera && !autoStartCameraView)
    const coverViewport = immersive || phoneAutoStart
    const showFloatingReset =
      resetPlacement === "panel-header" && canEdit && exists && open && !previzSaving && !closeBusy
    const showFloatingClose =
      Boolean(nested) && resetPlacement === "panel-header" && open && !previzSaving && !closeBusy
    const closeLabel = t("locationsPage.scene3d.close")
    const contained = Boolean(nested) && !coverViewport
    const fitEntityContent = !coverViewport && !contained

    const onConfirmSavePath = useCallback(() => {
      pathConfirmSavingRef.current = true
      void (async () => {
        if (closeBusyRef.current) {
          pathConfirmSavingRef.current = false
          return
        }
        if (sceneRef.current?.isExporting()) {
          pathConfirmSavingRef.current = false
          setPathConfirmOpen(false)
          setImmersive(false)
          onOpenChangeRef.current(false)
          return
        }
        closeBusyRef.current = true
        setCloseBusy(true)
        setPathConfirmOpen(false)
        try {
          const saved = (await sceneRef.current?.savePathPreviz()) ?? false
          if (!saved) return
          await sceneRef.current?.uploadPosterIfNeeded()
          setImmersive(false)
          onOpenChangeRef.current(false)
        } finally {
          pathConfirmSavingRef.current = false
          closeBusyRef.current = false
          setCloseBusy(false)
        }
      })()
    }, [])

    return (
      <>
        <WorkspaceFullscreenPanel
          open={open}
          onOpenChange={(next) => {
            if (next) {
              onOpenChange(true)
              return
            }
            if (preventDismiss || closeBusy) return
            beginClose()
          }}
          nested={nested}
          fitEntityContent={fitEntityContent}
          contained={contained}
          hideHeader
          title={t("locationsPage.scene3d.title", { name: locationName })}
          description={t("locationsPage.scene3d.description")}
          bodyClassName="min-h-0 overflow-hidden p-0"
          contentClassName={coverViewport ? "!inset-0 !top-0 !h-[100dvh] max-md:!h-[100dvh]" : undefined}
          onEscapeKeyDown={
            preventDismiss || coverViewport || closeBusy || pathConfirmOpen
              ? (event) => {
                  event.preventDefault()
                }
              : undefined
          }
        >
          {open ? (
            <div className="relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col">
              {showFloatingClose || showFloatingReset ? (
                <div
                  className={cn(
                    "pointer-events-none absolute top-12 right-2.5 z-[40] flex items-center gap-1.5",
                    coverViewport && "hidden",
                  )}
                >
                  {showFloatingReset ? (
                    <button
                      type="button"
                      className={FLOATING_CHROME_BTN}
                      title={t("locationsPage.scene3d.reset")}
                      aria-label={t("locationsPage.scene3d.reset")}
                      onClick={() => sceneRef.current?.requestReset()}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  ) : null}
                  {showFloatingClose ? (
                    <button
                      type="button"
                      className={FLOATING_CHROME_BTN}
                      aria-label={closeLabel}
                      onClick={() => beginClose()}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  ) : null}
                </div>
              ) : null}
              {waitingForScene ? (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black">
                  <button
                    type="button"
                    className={cn(
                      "pointer-events-auto absolute top-[max(0.75rem,env(safe-area-inset-top))]",
                      "right-[max(0.75rem,env(safe-area-inset-right))]",
                      "flex h-8 w-8 items-center justify-center rounded-full",
                      "border border-white/20 bg-black/45 text-white/90",
                    )}
                    aria-label={t("locationsPage.scene3d.deviceCamera.disable")}
                    onClick={() => {
                      setPhoneBootDismissed(true)
                      onDeviceCameraExit?.()
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <Loader2 className="h-6 w-6 animate-spin text-white/80" />
                </div>
              ) : null}
              {mountScene ? (
                <LocationScene3d
                  key={`${sceneMode}-${scene3dId ?? previzId ?? "none"}`}
                  ref={sceneRef}
                  projectId={projectId}
                  locationId={locationId}
                  locationName={locationName}
                  cast={cast}
                  props={props}
                  canEdit={canEdit}
                  onLayoutChanged={onLayoutChanged}
                  frameFormat={frameFormat}
                  shotId={shotId}
                  presetCharacterIds={presetCharacterIds}
                  onPrevizSaved={onPrevizSaved}
                  onPrevizCreateStart={onPrevizCreateStart}
                  onPrevizSaveFailed={onPrevizSaveFailed}
                  onViewpointSaved={onViewpointSaved}
                  captureHints={captureHints}
                  onExportingChange={setPrevizSaving}
                  nested={nested}
                  onImmersiveChange={setImmersive}
                  onExistsChange={handleExistsChange}
                  autoStartDeviceCamera={phoneBootDismissed ? false : autoStartDeviceCamera}
                  autoStartCameraView={phoneBootDismissed ? false : autoStartCameraView}
                  onPhoneFlyEnter={onPhoneFlyEnter}
                  onDeviceCameraExit={onDeviceCameraExit}
                  onRequestClose={preventDismiss ? undefined : beginClose}
                  sceneMode={sceneMode}
                  scene3dId={scene3dId}
                  previzId={previzId}
                  initialSceneState={initialSceneState}
                  initialHasVideo={initialHasVideo}
                  initialHasPoster={initialHasPoster}
                  modelLabel={modelLabel}
                  sceneName={sceneName}
                  onSceneNameSaved={onSceneNameSaved}
                  dismissAfterPrevizSave={dismissAfterPrevizSave}
                  onObjectCatalogChange={onObjectCatalogChange}
                  onObjectPathModeChange={onObjectPathModeChange}
                  onDismissAfterPrevizCapture={
                    dismissAfterPrevizSave
                      ? () => {
                          onPrevizCapture?.()
                          onOpenChange(false)
                        }
                      : undefined
                  }
                />
              ) : null}
            </div>
          ) : null}
        </WorkspaceFullscreenPanel>

        <ConfirmActionDialog
          open={pathConfirmOpen}
          onOpenChange={(open) => {
            setPathConfirmOpen(open)
            if (open || leaveResolveRef.current == null) return
            if (leaveHandledRef.current) {
              leaveHandledRef.current = false
              return
            }
            settleLeave(false)
          }}
          title={t("locationsPage.scene3d.closeUnsavedPrevizTitle")}
          message={t("locationsPage.scene3d.closeUnsavedPrevizMessage")}
          confirmLabel={t("locationsPage.scene3d.closeUnsavedPrevizSave")}
          alternativeLabel={t("locationsPage.scene3d.closeUnsavedPrevizDiscard")}
          onAlternative={() => {
            if (leaveResolveRef.current == null) {
              void finishClose()
              return
            }
            leaveHandledRef.current = true
            void flushLeave(true)
          }}
          onConfirm={() => {
            if (leaveResolveRef.current == null) {
              onConfirmSavePath()
              return
            }
            leaveHandledRef.current = true
            savePathAndLeave()
          }}
        />
      </>
    )
  },
)
